import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';
import { z } from 'zod';
import { PillButton } from '@/components/entity';
import { Form, FormMessage, TextInputField } from '@/components/form';
import { Skeleton } from '@/components/feedback';
import { useFormController } from '@/lib/forms/formController';
import {
  addAvailabilityAlertSubscriberForUser,
  countPendingAvailabilityAlertEmailsForUser,
  listAvailabilityAlertSubscribersForUser,
} from '@/lib/services/availabilityAlerts';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';

const EMAIL_SCHEMA = z.object({
  email: z.string().trim().email('Enter a valid email'),
});

type EmailValues = z.infer<typeof EMAIL_SCHEMA>;

const EMPTY_VALUES: EmailValues = { email: '' };

export function AvailabilityAlertsSubscriberAddForm(): JSX.Element {
  const user = useDashboardUser();
  const queryClient = useQueryClient();
  const [addSuccess, setAddSuccess] = useState(false);

  const totalSubscribersQuery = useQuery({
    queryKey: ['availability-alert-subscribers'],
    queryFn: async ({ signal }) => {
      return await listAvailabilityAlertSubscribersForUser({ user, signal });
    },
  });

  const pendingCountQuery = useQuery({
    queryKey: ['availability-alert-outbox', 'pending-count'],
    queryFn: async ({ signal }) => {
      return await countPendingAvailabilityAlertEmailsForUser({ user, signal });
    },
  });

  const addMutation = useMutation({
    mutationFn: async (values: EmailValues) => {
      return await addAvailabilityAlertSubscriberForUser({ user, email: values.email });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['availability-alert-subscribers'] }),
        queryClient.invalidateQueries({ queryKey: ['availability-alert-outbox'] }),
      ]);
    },
  });

  const form = useFormController<EmailValues>({
    initialValues: EMPTY_VALUES,
    schema: EMAIL_SCHEMA,
    onSubmit: async (values) => {
      await addMutation.mutateAsync(values);
    },
    onSuccess: () => {
      setAddSuccess(true);
    },
    mapSubmitError: (error) => ({
      message: error instanceof Error ? error.message : 'Unable to add subscriber.',
    }),
  });

  useEffect(() => {
    if (form.isDirty) {
      setAddSuccess(false);
    }
  }, [form.isDirty]);

  const emailField = form.field('email');

  const pendingCount =
    typeof pendingCountQuery.data === 'number' && Number.isFinite(pendingCountQuery.data)
      ? pendingCountQuery.data
      : 0;
  const totalCount = Array.isArray(totalSubscribersQuery.data)
    ? totalSubscribersQuery.data.length
    : 0;
  const countsLoading = totalSubscribersQuery.isLoading || pendingCountQuery.isLoading;

  return (
    <div className="rounded-2xl border border-surface-subtle bg-surface-subtle/30 p-4">
      <Form
        gap="sm"
        onSubmit={async (event) => {
          const succeeded = await form.handleSubmit(event);
          if (succeeded) {
            form.reset(EMPTY_VALUES);
          }
        }}
      >
        <TextInputField
          id="availability-alert-add-email"
          label="Add email"
          required
          type="email"
          value={emailField.value}
          onChange={emailField.onChange}
          onBlur={emailField.onBlur}
          error={form.touched.has('email') ? form.errors.email : undefined}
          placeholder="team@example.com"
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <PillButton type="submit" disabled={form.isSubmitting}>
            Add subscriber
          </PillButton>
          {countsLoading ? (
            <div className="flex items-center gap-2" aria-label="Loading subscriber counts">
              <Skeleton className="h-4 w-16 rounded-md" />
              <Skeleton className="h-4 w-16 rounded-md" />
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 text-xs text-text-primary/60">
              <span>{`${totalCount.toString()} total`}</span>
              <span aria-hidden="true">-</span>
              <span>{`${pendingCount.toString()} pending`}</span>
            </div>
          )}
        </div>

        {typeof form.submitError === 'string' && form.submitError.length > 0 ? (
          <FormMessage tone="error" role="alert">
            {form.submitError}
          </FormMessage>
        ) : null}
        {addSuccess ? (
          <FormMessage tone="success" role="status">
            Subscriber saved.
          </FormMessage>
        ) : null}
      </Form>
    </div>
  );
}
